import { LoggerService } from '@angular-ru/cdk/logger';
import { Clipboard } from '@angular/cdk/clipboard';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, interval, takeWhile } from 'rxjs';
import { ISocialSharingButton } from 'src/app/api/modules/core/dynamic/components/ISocialSharingButton';
import { IFaIcon } from 'src/app/api/modules/icons/fa/IFaIcon';
import { createDigitaServiceError } from 'src/app/app-error';
import { FaIconComponent } from '../../../../icons/components/fa-icon/fa-icon.component';
import { AbstractLinkComponent } from '../../../components/abstract/abstract-link/abstract-link.component';
import { SnackbarService } from '../../../services/snackbar.service';

/**
 * The icon for each recognized platform. The platform name doubles as the
 * button's style class (see the component SCSS), so an entry here plus a
 * color in the stylesheet is all a new platform needs.
 */
const PLATFORM_ICONS: Record<string, IFaIcon> = {
  blogger: { name: 'fab:blogger-b' },
  buffer: { name: 'fab:buffer' },
  copy: { name: 'fas:copy' },
  'copy-link': { name: 'fas:link' },
  diaspora: { name: 'fab:diaspora' },
  email: { name: 'fas:envelope' },
  facebook: { name: 'fab:facebook-f' },
  'facebook-messenger': { name: 'fab:facebook-messenger' },
  flipboard: { name: 'fab:flipboard' },
  instagram: { name: 'fab:instagram' },
  line: { name: 'fab:line' },
  linkedin: { name: 'fab:linkedin-in' },
  pinterest: { name: 'fab:pinterest-p' },
  pocket: { name: 'fab:get-pocket' },
  qq: { name: 'fab:qq' },
  reddit: { name: 'fab:reddit-alien' },
  sinaweibo: { name: 'fab:weibo' },
  sms: { name: 'fas:comment-sms' },
  tel: { name: 'fas:phone' },
  telegram: { name: 'fab:telegram' },
  tiktok: { name: 'fab:tiktok' },
  tumblr: { name: 'fab:tumblr' },
  twitch: { name: 'fab:twitch' },
  twitter: { name: 'fab:x-twitter' },
  viber: { name: 'fab:viber' },
  vk: { name: 'fab:vk' },
  whatsapp: { name: 'fab:whatsapp' },
  youtube: { name: 'fab:youtube' },
};

/**
 * The SocialSharingButtonComponent binds the URLs/Platform/Icon/Styles for a specific social network provider.
 *
 * This is done to reduce the end bundle size and to simplify the JSON data.
 *
 * A platform this build does not recognize (for example a network that has since been retired
 * but survives in an old stored configuration) renders as a neutral question-mark button —
 * the link still works, it just loses its brand identity.
 */
@Component({
  selector: 'app-social-sharing-button',
  templateUrl: './social-sharing-button.component.html',
  styleUrls: ['./social-sharing-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AbstractLinkComponent, FaIconComponent],
})
export class SocialSharingButtonComponent implements OnInit {
  private readonly clipboard = inject(Clipboard);
  private readonly destroyRef = inject(DestroyRef);
  private readonly logger = inject(LoggerService);
  private readonly snackbarService = inject(SnackbarService);

  /**
   * The Input Config as supplied by JSON.
   */
  readonly config = input.required<ISocialSharingButton>();

  /**
   * Set by the Social Sharing component that this button belongs to
   * depending on what type of group is being created.
   */
  readonly type = input<'share' | 'homepage'>('share');

  /**
   * The platform's visual identity: its style class and icon.
   *
   * Unrecognized platforms warn and fall back to the neutral question-mark
   * identity rather than failing fatally.
   */
  protected readonly identity = computed<{ styleClass: string; icon: IFaIcon }>(() => {
    const platform = this.config().platform;
    const icon = PLATFORM_ICONS[platform];
    if (!icon) {
      this.logger.warn(`[Social Sharing Button] - the ${platform} platform is not recognized. Rendering a neutral button instead.`);
      return { styleClass: 'undefined-button', icon: { name: 'fas:circle-question' } };
    }
    return { styleClass: platform, icon };
  });

  /**
   * The classes of the button: the shared base, the platform's brand color,
   * and the homepage shape when part of a homepage group.
   */
  protected readonly classes = computed(() => {
    const classes = ['social-button', this.identity().styleClass];
    if (this.type() === 'homepage') {
      classes.push('homepage');
    }
    return classes;
  });

  /**
   * Lifecycle Hook
   */
  ngOnInit() {
    // a missing platform is a malformed configuration and fails fatally
    // (unlike an unrecognized one, which renders the fallback identity)
    if (!this.config().platform) {
      throw createDigitaServiceError(`SocialSharingButton`, `configure`, `No "platform" provided but this is required.`, `config`);
    }
  }

  /**
   * Occurs when the abstract link captures a click it does not navigate for.
   *
   * Only the clipboard platforms act on it — every other platform navigates
   * via its link inside the abstract link itself.
   */
  protected onClicked() {
    const { platform, link, snackbar } = this.config();

    if (!link?.href || (platform !== 'copy' && platform !== 'copy-link')) {
      return;
    }

    // the CDK clipboard may need several attempts for large payloads
    const pending = this.clipboard.beginCopy(link.href);
    let remainingAttempts = 5;
    interval(100)
      .pipe(
        takeWhile(() => --remainingAttempts > 0 && !pending.copy()),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          pending.destroy();
          if (snackbar) {
            this.snackbarService.open(snackbar);
          }
        }),
      )
      .subscribe();
  }
}
